import { isAxiosError } from "axios";
import { format } from "date-fns";
import saveAs from "file-saver";
import i18n, { t } from "i18next";
import axios from "../../../lib/axios";
import {
  PerformanceToConfirmEntry,
  PresenceAvailableEmployee,
  PresenceAvailableProject,
} from "../../../types";
import { PresenceEntry, PresenceResponse } from "../types/Presence";

export const getPresenceForUser = async (
  year: number,
  month: number,
): Promise<PresenceEntry[]> => {
  try {
    const { data } = await axios.get<PresenceEntry[]>(
      "/api/v2/presence-for-user",
      {
        headers: {
          "Accept-Language": i18n.language,
        },
        params: { year, month },
      },
    );

    return data;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const getPresence = async (
  year: number,
  month: number,
  companyId?: number,
  pageNumber?: number,
  pageSize?: number,
  search?: string,
): Promise<PresenceResponse> => {
  try {
    const { data } = await axios.get<PresenceResponse>(
      "/api/v2/presence-for-management-staff",
      {
        headers: {
          "Accept-Language": i18n.language,
        },
        params: { year, month, companyId, pageNumber, pageSize, search },
      },
    );

    return data;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const getPresencesToConfirm = async (
  companyId?: number,
  from?: string,
  to?: string,
): Promise<PerformanceToConfirmEntry[]> => {
  try {
    const { data } = await axios.get<PerformanceToConfirmEntry[]>(
      "/api/v2/presences-to-confirm",
      {
        headers: {
          "Accept-Language": i18n.language,
        },
        params: {
          companyId,
          from,
          to,
        },
      },
    );

    return data;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const confirmPresencePositions = async (
  presencePositionIds: number[],
): Promise<void> => {
  try {
    await axios.post("/api/v2/presences-confirm", presencePositionIds, {
      headers: {
        "Accept-Language": i18n.language,
      },
    });
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const rejectPresencePositions = async (
  presencePositionIds: number[],
): Promise<string | undefined> => {
  try {
    const response = await axios.post<{ warning?: string } | null>(
      "/api/v2/presences-reject",
      presencePositionIds,
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return response.data?.warning ?? undefined;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const getPresenceAvailableEmployees = async (
  companyId?: number,
  from?: string,
  to?: string,
): Promise<PresenceAvailableEmployee[]> => {
  try {
    const { data } = await axios.get<PresenceAvailableEmployee[]>(
      "/api/v2/presence-available-employees",
      {
        headers: {
          "Accept-Language": i18n.language,
        },
        params: {
          companyId,
          from,
          to,
        },
      },
    );

    return data;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const getPresenceAvailableProjects = async (
  companyId?: number,
): Promise<PresenceAvailableProject[]> => {
  try {
    const { data } = await axios.get<PresenceAvailableProject[]>(
      "/api/v2/presence-available-projects",
      {
        headers: {
          "Accept-Language": i18n.language,
        },
        params: {
          companyId,
        },
      },
    );

    return data;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const exportPresenceXlsx = async (
  year: number,
  month: number,
  companyId?: number,
): Promise<void> => {
  try {
    const response = await axios.get<Blob>("/api/v2/presence-export-xls", {
      headers: {
        "Accept-Language": i18n.language,
      },
      params: { year, month, companyId },
      responseType: "blob",
    });

    const fileName = `Obecnosci_${year}-${String(month).padStart(2, "0")}.xlsx`;
    saveAs(
      new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      fileName,
    );
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          // gdy serwer zwraca JSON z błędem zamiast XLSX, axios nadal opakuje to w Blob
          if (error.response.data instanceof Blob) {
            try {
              const asText = await error.response.data.text();
              const parsed = JSON.parse(asText);
              if (parsed && typeof parsed === "object" && "message" in parsed) {
                message = String(parsed.message);
              }
            } catch {
              // ignoruj — zostawiamy domyślny komunikat
            }
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const createOrUpdatePresence = async (data: {
  employees: {
    companyId: number;
    employeeId: number;
  }[];
  fromDate: string;
  toDate: string;
  positions: {
    fromHour: string;
    toHour: string;
    project: {
      id: number;
      name: string;
    };
    note: string;
  }[];
  skipWeekendsAndHolidays: boolean;
}): Promise<string | undefined> => {
  try {
    const response = await axios.put<{ warning?: string }>(
      "/api/v2/presences",
      data,
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return response.data?.warning;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
  
};

export const createPresencePosition = async (data: {
  companyId: number;
  employeeId: number;
  date: string;
  from: string;
  to: string;
  projectId: number;
  project: string;
  note: string;
}): Promise<string | undefined> => {
  try {
    const response = await axios.post<{ warning?: string }>(
      "/api/presence-position",
      data,
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return response.data?.warning;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const updatePresencePosition = async (
  id: number,
  data: {
    date: string;
    from: string;
    to: string;
    projectId: number | null;
    project: string | null;
    note: string | null;
  },
): Promise<string | undefined> => {
  try {
    const response = await axios.put<{ warning?: string }>(
      `/api/presence-position/${id}`,
      data,
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return response.data?.warning;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const exportSummaryOfHoursByProjectsToXlsx = async (
  from: Date,
  to: Date,
  companyId?: number,
): Promise<void> => {
  try {
    const response = await axios.get<Blob>(
      "/api/presences/summary-of-hours-by-project/export-xls",
      {
        headers: {
          "Accept-Language": i18n.language,
        },
        params: {
          from: format(from, "yyyy-MM-dd"),
          to: format(to, "yyyy-MM-dd"),
          companyId,
        },
        responseType: "blob",
      },
    );

    const fileName = `WedlugProjektow_Wszystkie_${new Date().toISOString().split("T")[0]}.xlsx`;
    saveAs(
      new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      fileName,
    );
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (error.response.data instanceof Blob) {
            try {
              const asText = await error.response.data.text();
              const parsed = JSON.parse(asText);
              if (parsed && typeof parsed === "object" && "message" in parsed) {
                message = String(parsed.message);
              }
            } catch {
              // ignoruj
            }
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const exportSummaryOfHoursByProjectsSingleSheetToXlsx = async (
  from: Date,
  to: Date,
  companyId?: number,
): Promise<void> => {
  try {
    const response = await axios.get<Blob>(
      "/api/presences/summary-of-hours-by-project/export-xls-single-sheet",
      {
        headers: {
          "Accept-Language": i18n.language,
        },
        params: {
          from: format(from, "yyyy-MM-dd"),
          to: format(to, "yyyy-MM-dd"),
          companyId,
        },
        responseType: "blob",
      },
    );

    const fileName = `WedlugProjektow_JednaTabela_${new Date().toISOString().split("T")[0]}.xlsx`;
    saveAs(
      new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      fileName,
    );
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (error.response.data instanceof Blob) {
            try {
              const asText = await error.response.data.text();
              const parsed = JSON.parse(asText);
              if (parsed && typeof parsed === "object" && "message" in parsed) {
                message = String(parsed.message);
              }
            } catch {
              // ignoruj
            }
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};